import { DetailedCellError, HyperFormula } from 'hyperformula';
// @ts-expect-error
import frFR from 'hyperformula/i18n/languages/frFR';
// @ts-expect-error
import ptPt from 'hyperformula/i18n/languages/ptPT';
import { evaluate as engineEvaluate, evaluateBatch as engineEvaluateBatch } from './engine';
import { getFunctionMapping, getUnifiedReverseFunctionMapping } from './engine/i18n';
import { removeAccents } from 'common/utils/removeAccents';
// WARNING: This file is used also by backend, DO NOT add external libraries without understanding if it will work

// ============================================================================
// ENGINE TOGGLE: Set to true to use the custom lightweight engine,
//                set to false to use HyperFormula.
// ============================================================================
const USE_CUSTOM_ENGINE = true;

HyperFormula.registerLanguage('frFR', frFR);
HyperFormula.registerLanguage('ptPT', ptPt);

// Check if running in backend (V8) context
const isBackend = typeof window === 'undefined';

const getLanguage: () => string = isBackend
    ? () => 'pt'
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    : (() => { const i18n = require('common/services/I18n').default; return () => (i18n.language as string) ?? 'pt'; })();

export type FormatType = 'plain' | 'money' | 'percentage';

export interface ExpressionResult {
    value: number;
    stringValue?: string | null;
    error: boolean;
    errorMessage?: string;
}

export interface ExpressionParameter {
    id: string;
    serializedId?: string; // Pre-serialized ID for variable map lookup (avoids repeated regex)
    name: string;
    value: string | number | number[] | null;
    hidden?: boolean;
}

export const EXPRESSION_PARAMETER_MENTION_PREFIX = 'M_';
export const EXPRESSION_PARAMETER_MENTION_SUFFIX = '_E';

class ExpressionSystem {
    private strToNumber (str: string | null | undefined) {
        const parsedValue = Number(str || 0);
        return isNaN(parsedValue) ? 0 : parsedValue;
    }

    public numberToStr (value: number | null | undefined) {
        return value?.toString() || '0';
    }

    private isExpressionValid (expr: string) {
        if (!isNaN((expr as unknown) as number) && expr.trim() !== '') return true;

        return expr.trim().startsWith('=');
    }

    public serializeParameterMentionId (id: string) {
        return EXPRESSION_PARAMETER_MENTION_PREFIX + id.replace(/[^0-9A-Za-z_]/g, '') + EXPRESSION_PARAMETER_MENTION_SUFFIX;
    }

    public formatParameterName (names: string[]) {
        return names.join(' > ');
    }

    public formatParameterId (names: string[]) {
        return names.join('__');
    }

    public extractMentionIds (expression: string): string[] {
        const regex = /M_[0-9A-Za-z_]+_E/g;
        return expression.match(regex) ?? [];
    }

    public hasCircularReference (
        currentMentionId: string,
        expression: string,
        formulasByMentionId: Map<string, string | null>
    ): boolean {
        const visited = new Set<string>();
        const queue = this.extractMentionIds(expression);

        while (queue.length > 0) {
            const id = queue.shift()!;
            if (id === currentMentionId) return true;
            if (visited.has(id)) continue;
            visited.add(id);

            const formula = formulasByMentionId.get(id);
            if (formula) {
                queue.push(...this.extractMentionIds(formula));
            }
        }

        return false;
    }

    /**
     * Translate function names in a formula from any language to the target language.
     * Identifies function names as identifiers followed by '(' — M_xxx_E params are excluded
     * because they are never followed by '('.
     */
    public translateFormula (formula: string, targetLang: string): string {
        if (!formula) return formula;

        const unifiedReverse = getUnifiedReverseFunctionMapping();
        const forwardMap = targetLang === 'en' ? null : getFunctionMapping(targetLang);

        // Match identifiers (including accented chars and dots) followed by '('
        return formula.replace(
            /([A-Za-z_\u00C0-\u024F][A-Za-z0-9_.\u00C0-\u024F]*)(\s*\()/g,
            (_match, name: string, paren: string) => {
                // Try to find the English key for this function name
                const upper = name.toUpperCase();
                let englishKey = unifiedReverse[upper];

                // Also try accent-stripped version
                if (!englishKey) {
                    const stripped = removeAccents(name).toUpperCase();
                    englishKey = unifiedReverse[stripped];
                }

                if (!englishKey) return name + paren; // Not a known function, leave as-is

                if (targetLang === 'en') return englishKey + paren;

                const translated = forwardMap?.[englishKey];
                return (translated || englishKey) + paren;
            }
        );
    }

    /**
     * Normalize a formula to canonical English function names for storage.
     */
    public normalizeToEnglish (formula: string): string {
        return this.translateFormula(formula, 'en');
    }

    public formatExpressionForDisplay (expression: string, parameters: ExpressionParameter[], lang?: string): string {
        if (!expression) return '';

        // Replace each M_..._E token with @parameterName (like in the modal)
        let result = expression;
        for (const param of parameters) {
            const serializedId = this.serializeParameterMentionId(param.id);
            if (result.includes(serializedId)) {
                // Escape special regex characters in the serialized ID
                const escapedId = serializedId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                result = result.replace(new RegExp(escapedId, 'g'), `@${param.name}`);
            }
        }
        result = this.translateFormula(result, lang ?? getLanguage());
        return result;
    }

    // ========================================================================
    // PUBLIC EVALUATE — routes to custom engine or HyperFormula based on flag
    // ========================================================================

    public evaluate (expression: string, expressionParameters: ExpressionParameter[]): ExpressionResult {
        if (USE_CUSTOM_ENGINE) {
            return this.evaluateCustom(expression, expressionParameters);
        }
        return this.evaluateHF(expression, expressionParameters);
    }

    public evaluateBatch (expressions: string[], expressionParameters: ExpressionParameter[]): ExpressionResult[] {
        if (USE_CUSTOM_ENGINE) {
            return this.evaluateBatchCustom(expressions, expressionParameters);
        }
        return this.evaluateBatchHF(expressions, expressionParameters);
    }

    // ========================================================================
    // CUSTOM ENGINE IMPLEMENTATION
    // ========================================================================

    private evaluateCustom (expression: string, expressionParameters: ExpressionParameter[]): ExpressionResult {
        try {
            let expr = (expression ?? '0').toString().trim();

            if (!expr) {
                expr = '0';
            }

            expr = `=${expr}`;

            if (!this.isExpressionValid(expr)) {
                const errorMessage = 'Invalid input: Only numbers or expressions allowed.';
                console.error(errorMessage);
                return {
                    value: 0,
                    error: true,
                    errorMessage
                };
            }

            const lang = getLanguage();
            const variables = this.buildVariableMap(expressionParameters);
            const result = engineEvaluate(expr.substring(1), variables, lang);

            return {
                value: this.strToNumber(result.value?.toString()) ?? 0,
                stringValue: result.stringValue,
                error: result.error,
                errorMessage: result.errorMessage
            };
        } catch (error) {
            const errorMessage = (error as Error)?.message ?? 'Unknown error';
            console.warn('Error evaluating expression', error);
            return {
                value: 0,
                error: true,
                errorMessage
            };
        }
    }

    private evaluateBatchCustom (expressions: string[], expressionParameters: ExpressionParameter[]): ExpressionResult[] {
        if (expressions.length === 0) return [];

        try {
            const lang = getLanguage();
            const variables = this.buildVariableMap(expressionParameters);

            // Prepare expressions: prepend = and validate
            const preparedExprs = expressions.map(e => {
                let expr = (e ?? '0').toString().trim();
                if (!expr) expr = '0';
                expr = `=${expr}`;
                return this.isExpressionValid(expr) ? expr.substring(1) : '0';
            });

            const results = engineEvaluateBatch(preparedExprs, variables, lang);

            return results.map((r: { value: number; stringValue?: string | null; error: boolean; errorMessage?: string }) => ({
                value: this.strToNumber(r.value?.toString()) ?? 0,
                stringValue: r.stringValue,
                error: r.error,
                errorMessage: r.errorMessage
            }));
        } catch (error) {
            console.warn('Error in batch evaluation', error);
            return expressions.map(() => ({ value: 0, error: true, errorMessage: (error as Error)?.message ?? 'Unknown error' }));
        }
    }

    private buildVariableMap (expressionParameters: ExpressionParameter[]): Map<string, number | number[] | string | null> {
        const variables = new Map<string, number | number[] | string | null>();

        for (const exprParam of expressionParameters) {
            const paramName = exprParam.serializedId ?? this.serializeParameterMentionId(exprParam.id);

            if (Array.isArray(exprParam.value)) {
                variables.set(paramName, exprParam.value);
            } else {
                const rawValue = exprParam.value;
                if (typeof rawValue === 'string') {
                    if (rawValue === '') {
                        variables.set(paramName, rawValue);
                    } else {
                        const parsed = Number(rawValue);
                        variables.set(paramName, !isNaN(parsed) ? parsed : rawValue);
                    }
                } else {
                    variables.set(paramName, rawValue);
                }
            }
        }

        return variables;
    }

    // ========================================================================
    // HYPERFORMULA IMPLEMENTATION
    // ========================================================================

    private evaluateHF (expression: string, expressionParameters: ExpressionParameter[]): ExpressionResult {
        try {
            let expr = (expression ?? '0').toString().trim();

            if (!expr) {
                expr = '0';
            }

            expr = `=${expr}`;

            if (!this.isExpressionValid(expr)) {
                const errorMessage = 'Invalid input: Only numbers or expressions allowed.';
                console.error(errorMessage);
                return {
                    value: 0,
                    error: true,
                    errorMessage
                };
            }

            const lang = getLanguage();
            const hf = HyperFormula.buildEmpty({
                licenseKey: 'gpl-v3',
                language: `${lang}${lang.toUpperCase()}`,

                dateFormats: ['DD/MM/YYYY'],
                timeFormats: ['hh:mm', 'hh:mm:ss.sss'],
                currencySymbol: ['€', 'EUR'],
                localeLang: `${lang}-${lang.toUpperCase()}`,
                functionArgSeparator: ';',
                decimalSeparator: ',',
                thousandSeparator: '',
                arrayColumnSeparator: ';',
                arrayRowSeparator: '|',
                nullYear: 30,
                caseSensitive: false,
                accentSensitive: true,
                ignorePunctuation: false,
                useWildcards: true,
                useRegularExpressions: false,
                matchWholeCell: true,
                useArrayArithmetic: true,
                ignoreWhiteSpace: 'any',
                evaluateNullToZero: true,
                leapYear1900: true,
                nullDate: { year: 1899, month: 12, day: 31 },
                smartRounding: true,
            });

            hf.suspendEvaluation();
            hf.addNamedExpression('TRUE', '=TRUE()');
            hf.addNamedExpression('FALSE', '=FALSE()');

            const sheetName = hf.addSheet('ExpressionSheet');
            const sheetId = hf.getSheetId(sheetName);

            hf.setCellContents({ sheet: sheetId!, row: 0, col: 0 }, [['result', expr]]);

            let currentRow = 0;
            for (const exprParam of expressionParameters) {
                const paramName = this.serializeParameterMentionId(exprParam.id);

                if (Array.isArray(exprParam.value)) {
                    const startRow = currentRow;
                    for (let i = 0; i < exprParam.value.length; i++) {
                        hf.setCellContents({ sheet: sheetId!, row: currentRow, col: 2 }, [[exprParam.value[i]]]);
                        currentRow++;
                    }
                    const endRow = currentRow - 1;
                    hf.addNamedExpression(paramName, `=ExpressionSheet!$C$${startRow + 1}:$C$${endRow + 1}`);
                } else {
                    let value = exprParam.value;
                    if (typeof value === 'string') {
                        const parsed = Number(value);
                        if (!isNaN(parsed)) {
                            value = parsed;
                        }
                    }
                    hf.addNamedExpression(paramName, value);
                }
            }

            hf.resumeEvaluation();

            const result = hf.getCellValue({ sheet: sheetId!, row: 0, col: 1 });

            if (result instanceof DetailedCellError) {
                const errorMessage = `${result.type}: ${result.message}`;
                return {
                    value: 0,
                    error: true,
                    errorMessage
                };
            }

            return {
                value: this.strToNumber(result?.toString()) ?? 0,
                error: false
            };
        } catch (error) {
            const errorMessage = (error as Error)?.message ?? 'Unknown error';
            console.warn('Error evaluating expression', error);
            return {
                value: 0,
                error: true,
                errorMessage
            };
        }
    }

    private evaluateBatchHF (expressions: string[], expressionParameters: ExpressionParameter[]): ExpressionResult[] {
        if (expressions.length === 0) return [];

        try {
            const lang = getLanguage();
            const hf = HyperFormula.buildEmpty({
                licenseKey: 'gpl-v3',
                language: `${lang}${lang.toUpperCase()}`,
                dateFormats: ['DD/MM/YYYY'],
                timeFormats: ['hh:mm', 'hh:mm:ss.sss'],
                currencySymbol: ['€', 'EUR'],
                localeLang: `${lang}-${lang.toUpperCase()}`,
                functionArgSeparator: ';',
                decimalSeparator: ',',
                thousandSeparator: '',
                arrayColumnSeparator: ';',
                arrayRowSeparator: '|',
                nullYear: 30,
                caseSensitive: false,
                accentSensitive: true,
                ignorePunctuation: false,
                useWildcards: true,
                useRegularExpressions: false,
                matchWholeCell: true,
                useArrayArithmetic: true,
                ignoreWhiteSpace: 'any',
                evaluateNullToZero: true,
                leapYear1900: true,
                nullDate: { year: 1899, month: 12, day: 31 },
                smartRounding: true,
            });

            hf.suspendEvaluation();

            hf.addNamedExpression('TRUE', '=TRUE()');
            hf.addNamedExpression('FALSE', '=FALSE()');

            const sheetName = hf.addSheet('ExpressionSheet');
            const sheetId = hf.getSheetId(sheetName);

            const cellData: (string | number | null)[][] = [];
            for (let i = 0; i < expressions.length; i++) {
                let expr = (expressions[i] ?? '0').toString().trim();
                if (!expr) expr = '0';
                expr = `=${expr}`;
                cellData.push([`expr${i}`, this.isExpressionValid(expr) ? expr : '=0']);
            }
            hf.setCellContents({ sheet: sheetId!, row: 0, col: 0 }, cellData);

            let currentRow = 0;
            for (const exprParam of expressionParameters) {
                const paramName = this.serializeParameterMentionId(exprParam.id);

                if (Array.isArray(exprParam.value)) {
                    const startRow = currentRow;
                    for (let i = 0; i < exprParam.value.length; i++) {
                        hf.setCellContents({ sheet: sheetId!, row: currentRow, col: 2 }, [[exprParam.value[i]]]);
                        currentRow++;
                    }
                    const endRow = currentRow - 1;
                    hf.addNamedExpression(paramName, `=ExpressionSheet!$C$${startRow + 1}:$C$${endRow + 1}`);
                } else {
                    let value = exprParam.value;
                    if (typeof value === 'string') {
                        const parsed = Number(value);
                        if (!isNaN(parsed)) {
                            value = parsed;
                        }
                    }
                    hf.addNamedExpression(paramName, value);
                }
            }

            hf.resumeEvaluation();

            const results: ExpressionResult[] = [];
            for (let i = 0; i < expressions.length; i++) {
                const result = hf.getCellValue({ sheet: sheetId!, row: i, col: 1 });
                if (result instanceof DetailedCellError) {
                    results.push({ value: 0, error: true, errorMessage: `${result.type}: ${result.message}` });
                } else {
                    results.push({ value: this.strToNumber(result?.toString()) ?? 0, error: false });
                }
            }

            hf.destroy();
            return results;
        } catch (error) {
            console.warn('Error in batch evaluation', error);
            return expressions.map(() => ({ value: 0, error: true, errorMessage: (error as Error)?.message ?? 'Unknown error' }));
        }
    }
}

export default new ExpressionSystem();
